import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { Youtube, Trash2, ShieldCheck, RefreshCw, ExternalLink } from "lucide-react";

export default function Channels() {
    const qc = useQueryClient();
    const [name, setName] = useState("");
    const [handle, setHandle] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const yt = searchParams.get("yt");
        if (yt === "connected") {
            toast.success("Canal REAL de YouTube vinculado por OAuth");
            qc.invalidateQueries({ queryKey: ["channels"] });
        } else if (yt === "error") {
            toast.error(`OAuth falló: ${searchParams.get("reason") || "desconocido"}`);
        }
        if (yt) setSearchParams({}, { replace: true });
        // eslint-disable-next-line
    }, []);

    const { data: channels = [] } = useQuery({
        queryKey: ["channels"],
        queryFn: async () => (await api.get("/channels")).data,
    });

    const { data: integrations } = useQuery({
        queryKey: ["integrations"],
        queryFn: async () => (await api.get("/integrations")).data,
    });
    const googleReady = integrations?.google?.configured;

    const oauth = useMutation({
        mutationFn: async () => (await api.get("/youtube/auth-url")).data,
        onSuccess: (d) => {
            window.location.href = d.url;
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Configura Google Cloud en Integraciones"),
    });

    const sync = useMutation({
        mutationFn: async (id) => (await api.post(`/channels/${id}/sync`)).data,
        onSuccess: (d) => {
            toast.success(`Sync real: ${d.synced_videos} videos con métricas de YouTube Analytics`);
            qc.invalidateQueries({ queryKey: ["channels"] });
            qc.invalidateQueries({ queryKey: ["metrics"] });
            qc.invalidateQueries({ queryKey: ["compare"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo sincronizando"),
    });

    const connect = useMutation({
        mutationFn: async () => (await api.post("/channels/connect", { name, handle })).data,
        onSuccess: () => {
            toast.success("Canal conectado por OAuth (scope mínimo)");
            setName("");
            setHandle("");
            qc.invalidateQueries({ queryKey: ["channels"] });
        },
        onError: () => toast.error("Fallo conectando canal"),
    });

    const remove = useMutation({
        mutationFn: async (id) => (await api.delete(`/channels/${id}`)).data,
        onSuccess: () => {
            toast("Canal desconectado", { icon: "⚠" });
            qc.invalidateQueries({ queryKey: ["channels"] });
        },
    });

    return (
        <div data-testid="channels-page">
            <PageHead
                title="GESTIÓN MULTI-CANAL"
                sub="Conexión por OAuth oficial con permisos de menor privilegio (POLP). Sin creación automática de cuentas."
            />

            <Panel title="Vincular canal REAL (OAuth Google)" testId="oauth-panel" className="mb-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <p className="text-xs text-muted-foreground">
                        {googleReady
                            ? "Google Cloud configurado. Autoriza con tu cuenta y el canal se vincula con métricas reales de YouTube Analytics."
                            : "Configura tu Client ID / Secret de Google Cloud en Integraciones para vincular canales reales."}
                    </p>
                    {googleReady ? (
                        <Btn
                            testId="oauth-connect-btn"
                            variant="green"
                            onClick={() => oauth.mutate()}
                            disabled={oauth.isPending}
                        >
                            <span className="flex items-center gap-2">
                                <ExternalLink size={13} /> Vincular con YouTube
                            </span>
                        </Btn>
                    ) : (
                        <Link
                            to="/integrations"
                            data-testid="oauth-goto-integrations"
                            className="border border-primary px-4 py-2 text-xs uppercase tracking-wider text-primary hover:bg-primary hover:text-primary-foreground"
                        >
                            Ir a Integraciones →
                        </Link>
                    )}
                </div>
            </Panel>

            <Panel title="Conectar canal (manual / demo)" testId="channel-form" className="mb-6">
                <div className="flex flex-col gap-3 md:flex-row">
                    <input
                        data-testid="channel-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Nombre del canal"
                        className="flex-1 border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    />
                    <input
                        data-testid="channel-handle-input"
                        value={handle}
                        onChange={(e) => setHandle(e.target.value)}
                        placeholder="@handle"
                        className="flex-1 border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    />
                    <Btn
                        testId="channel-connect-btn"
                        onClick={() => name.trim() && handle.trim() && connect.mutate()}
                        disabled={connect.isPending || !name.trim() || !handle.trim()}
                    >
                        Conectar
                    </Btn>
                </div>
            </Panel>

            <div className="grid grid-cols-1 gap-px bg-border md:grid-cols-2 xl:grid-cols-3">
                {channels.map((c) => (
                    <div key={c.id} className="bg-card p-5" data-testid={`channel-card-${c.id}`}>
                        <div className="mb-4 flex items-center gap-3">
                            {c.avatar ? (
                                <img src={c.avatar} alt="" className="h-10 w-10 border border-border object-cover" />
                            ) : (
                                <div className="flex h-10 w-10 items-center justify-center border border-border text-primary">
                                    <Youtube size={18} />
                                </div>
                            )}
                            <div>
                                <div className="font-head text-base leading-none">{c.name}</div>
                                <div className="text-xs text-primary">{c.handle}</div>
                            </div>
                        </div>
                        <div className="mb-3 flex items-center justify-between text-xs">
                            <span className="flex items-center gap-2">
                                <StatusDot color="#39FF14" pulse /> {c.status}
                                {c.source === "youtube" && (
                                    <span
                                        className="border px-1.5 py-0.5 text-[9px] uppercase tracking-wider"
                                        style={{ color: "#FF003C", borderColor: "#FF003C" }}
                                        data-testid={`channel-real-badge-${c.id}`}
                                    >
                                        Real
                                    </span>
                                )}
                            </span>
                            <span className="text-muted-foreground">
                                {c.subscribers?.toLocaleString()} subs
                            </span>
                        </div>
                        <div className="mb-4 flex flex-wrap gap-1">
                            {(c.oauth_scope || []).map((s, i) => (
                                <span
                                    key={i}
                                    className="flex items-center gap-1 border border-border px-2 py-0.5 text-[10px] text-muted-foreground"
                                >
                                    <ShieldCheck size={10} className="text-primary" /> {s}
                                </span>
                            ))}
                        </div>
                        {c.source === "youtube" && (
                            <Btn
                                testId={`channel-sync-${c.id}`}
                                variant="ghost"
                                onClick={() => sync.mutate(c.id)}
                                disabled={sync.isPending}
                                className="mb-2 w-full"
                            >
                                <span className="flex items-center justify-center gap-2">
                                    <RefreshCw size={13} className={sync.isPending ? "animate-spin" : ""} />
                                    {sync.isPending ? "Sincronizando…" : "Sync métricas reales"}
                                </span>
                            </Btn>
                        )}
                        <Btn
                            testId={`channel-remove-${c.id}`}
                            variant="danger"
                            onClick={() => remove.mutate(c.id)}
                            className="w-full"
                        >
                            <span className="flex items-center justify-center gap-2">
                                <Trash2 size={13} /> Desconectar
                            </span>
                        </Btn>
                    </div>
                ))}
            </div>
        </div>
    );
}
